/* Author: Sai Sandeep Vaddi

Description
A wrapper hook around swr where we can push the data into recoil atoms

Usage
Call useSWRResponseToRecoil with the response of a SWR call and it’ll store it in atoms and also returns the same data. Basically a wrapper on top of useSWR.
You can specify recoil atoms for data, loading, and error (optional).

Example:

export const supportedTakeoverTypesLoadingAtom = atom<boolean>({
  key: `supportedTakeoverTypesLoadingAtom`,
  default: false,
});

export const supportedTakeoverTypesAtom = atom<string[]>({
  key: `supportedTakeoverTypesAtom`,
  default: [],
});

export function useSupportedCategoriesData(): {
  data: string[] | null;
  error: Error | null;
  isLoading: boolean;
} {
  const { data, error, isLoading } = useSWR<string[], Error>(
    getAPIEndpoint(OnRoadPerformanceType.SupportedCategories)
  );

  return useSWRResponseToRecoil(
    {
      data: data ?? null,
      error: error ?? null,
      isLoading,
    },
    {
      data: supportedTakeoverTypesAtom,
      loading: supportedTakeoverTypesLoadingAtom,
    }
  );
}

*/

import { uniqueId } from 'lodash';
import { useEffect } from 'react';
import { atom, RecoilState, useSetRecoilState } from 'recoil';
import { KeyedMutator } from 'swr';

export interface SWRToRecoilResponse<K> {
  data: K | null;
  error: Error | null;
  isLoading: boolean;
}

interface SWRRecoilState<K> {
  data: RecoilState<K>;
  loading?: RecoilState<boolean>;
  error?: RecoilState<Error | null>;
}

// Placeholder atoms to avoid conditional hooks issue when loader and error atoms are not passed
// We won't be setting them though
const _unusedLoaderAtom = atom<boolean>({
  key: uniqueId() + '__LoaderAtom',
  default: false,
});

const _unusedErrorAtom = atom<Error | null>({
  key: uniqueId() + '__ErrorAtom',
  default: null,
});

// A wrapper hook around swr where we can push the data into recoil atoms
function useSWRResponseToRecoil<
  K,
  T extends {
    data: K | null;
    isLoading: boolean;
    mutate?: KeyedMutator<K>;
    error: Error | null;
  }
>(swrHook: T, recoilAtoms: SWRRecoilState<K>): T {
  const { loading: loaderAtom, error: errorAtom, data: dataAtom } = recoilAtoms;
  const setDataAtom = useSetRecoilState(dataAtom);
  const setLoaderAtom = useSetRecoilState(loaderAtom ?? _unusedLoaderAtom);
  const setErrorAtom = useSetRecoilState(errorAtom ?? _unusedErrorAtom);

  const { data, isLoading, error } = swrHook;

  useEffect(() => {
    if (!loaderAtom) {
      return;
    }

    setLoaderAtom(isLoading);
  }, [isLoading, loaderAtom, setLoaderAtom]);

  useEffect(() => {
    if (!errorAtom) {
      return;
    }

    setErrorAtom(error);
  }, [error, errorAtom, setErrorAtom]);

  useEffect(() => {
    if (!dataAtom || isLoading || error || !data) {
      return;
    }
    setDataAtom(data);
  }, [data, dataAtom, error, isLoading, setDataAtom]);

  return swrHook;
}

export default useSWRResponseToRecoil;
